import React from 'react';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationEllipsis,
} from '@core-ui/Components/ui/pagination';
import { Button } from '@core-ui/Components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@core-ui/Components/ui/select';
import { useTranslation } from 'react-i18next';
import { cn } from '@core-ui-lib/utils.ts';
import { DOTS, usePaginationRange } from '@hooks/usePagination.ts';
import { type PaginationControlsProps,  } from '@types';

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  mode = 'client',
  totalItems,
  currentPage,
  onPageChange,
  rowsPerPage,
  setRowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 15, 25, 50],
  siblingCount = 1,
  isLoading = false,
  className,
}) => {
  const { t } = useTranslation();
  const { totalPages, paginationRange } = usePaginationRange({
    totalItems,
    currentPage,
    rowsPerPage,
    siblingCount,
  });

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  const safePageChange = (page: number): void => {
    if (page < 1 || page > totalPages || isLoading) {
      return;
    }
    onPageChange(page);
  };

  const handleRowsChange = (n: number): void => {
    if (onRowsPerPageChange != null) {
      onRowsPerPageChange(n);
    } else if (setRowsPerPage != null) {
      setRowsPerPage(n);
    }
  };

  if ((totalItems === 0) || totalPages === 0) {
    return null;
  }

  return (
    <div className={cn('w-full bg-card p-4', className)}>
      <Pagination
        aria-label="Pagination Navigation"
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        {/* Rows per page */}
        <div className="flex items-center gap-2 whitespace-nowrap text-sm">
          <span className="text-muted-foreground">
            {t('paginationControls.Rows per page')}:
          </span>
          <Select
            value={String(rowsPerPage)}
            onValueChange={(value) => {
              handleRowsChange(Number(value));
            }}
            disabled={isLoading}
          >
            <SelectTrigger data-qa-id="rows-per-page" className="w-20 rounded-md border border-border bg-muted text-foreground px-2 py-1 text-sm shadow-sm hover:bg-muted/70">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-50 w-20 rounded-md border-border bg-popover text-sm text-foreground shadow-xl">
              {rowsPerPageOptions.map((n) => (
                <SelectItem
                  key={n}
                  value={String(n)}
                  className="cursor-pointer px-2 py-1.5 hover:bg-muted/70 focus:bg-primary focus:text-primary-foreground aria-selected:bg-primary aria-selected:text-primary-foreground"
                >
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Numeric controls */}
        <PaginationContent className="flex flex-wrap items-center gap-1">
          <PaginationItem>
            <Button
              data-qa-id="first-button"
              variant="ghost"
              size="sm"
              onClick={() => {
                safePageChange(1);
              }}
              disabled={isFirstPage || isLoading}
              className="h-8 px-3 text-sm hover-fix cursor-pointer"
            >
              {t('paginationControls.First')}
            </Button>
          </PaginationItem>

          <PaginationItem>
            <Button
              data-qa-id="previous-button"
              variant="ghost"
              size="sm"
              onClick={() => {
                safePageChange(currentPage - 1);
              }}
              disabled={isFirstPage || isLoading}
              className="h-8 px-3 text-sm hover-fix cursor-pointer"
            >
              {t('paginationControls.Previous')}
            </Button>
          </PaginationItem>

          {paginationRange.map((page, idx) =>
            page === DOTS ? (
              <PaginationItem key={`dots-${idx}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={page}>
                <Button
                  variant={page === currentPage ? 'default' : 'ghost'}
                  onClick={() => {
                    safePageChange(Number(page));
                  }}
                  aria-current={page === currentPage ? 'page' : undefined}
                  className={cn(
                    'h-8 w-8 rounded-md text-sm font-medium transition-colors cursor-pointer',
                    page === currentPage
                      ? 'bg-primary text-primary-foreground shadow'
                      : 'hover-fix',
                  )}
                  disabled={isLoading}
                >
                  {page}
                </Button>
              </PaginationItem>
            ),
          )}

          <PaginationItem>
            <Button
              data-qa-id="next-button"
              variant="ghost"
              size="sm"
              onClick={() => {
                safePageChange(currentPage + 1);
              }}
              disabled={isLastPage || isLoading}
              className="h-8 px-3 text-sm hover-fix cursor-pointer"
            >
              {t('paginationControls.Next')}
            </Button>
          </PaginationItem>

          <PaginationItem>
            <Button
              data-qa-id="last-button"
              variant="ghost"
              size="sm"
              onClick={() => {
                safePageChange(totalPages);
              }}
              disabled={isLastPage || isLoading}
              className="h-8 px-3 text-sm hover-fix cursor-pointer"
            >
              {t('paginationControls.Last')}
            </Button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};
